import { ref, uploadString, getDownloadURL, deleteObject } from 'firebase/storage';
import { getStorage } from 'firebase/storage';
import { firebaseConfig } from '@/config/firebase';
import { initializeApp } from 'firebase/app';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getAuth, sendPasswordResetEmail, sendEmailVerification } from 'firebase/auth';
import userService from './UserService.js';

class FirebaseService {
  constructor() {
    this.app = initializeApp(firebaseConfig);
    this.storage = getStorage(this.app);
    this.functions = getFunctions(this.app);
    this.auth = getAuth(this.app);
    this.supportedImageResolutions = ["1920x1920", "640x640"];
  }

  /**
   * Get file path for current user
   */
  getFilePath(fileName) {
    return new Promise(async (resolve, reject) => {
      try {
        const currentUser = await userService.getCurrentUser();
        if (!currentUser) {
          throw new Error('No authenticated user found');
        }

        if (fileName.indexOf('/') === -1) {
          resolve(`${currentUser.uid}/${fileName}`);
        } else {
          resolve(fileName);
        }
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * Delete image files with multiple resolutions
   */
  async deleteImageFiles(fileName) {
    const jpgFileName = fileName.replace(".jpeg", ".jpeg").replace(".jpg", ".jpeg").replace(".png", ".jpeg");
    
    if (jpgFileName.indexOf(".jpeg") !== -1) {
      const fileNames = this.supportedImageResolutions.map(res => 
        jpgFileName.replace(".jpeg", `_${res}.jpeg`)
      );
      
      const promises = fileNames.map(async (fileName) => {
        try {
          return await this.deleteFile(fileName);
        } catch (error) {
          console.warn(`Failed to delete image file ${fileName}:`, error);
          return null; // Continue without failing
        }
      });
      
      const results = await Promise.allSettled(promises);
      const successfulDeletes = results.filter(result => result.status === 'fulfilled').length;
      const failedDeletes = results.filter(result => result.status === 'rejected').length;
      
      if (failedDeletes > 0) {
        console.log(`Deleted ${successfulDeletes} image files, ${failedDeletes} failed for ${fileName}`);
      }
      
      return results;
    } else {
      try {
        return await this.deleteFile(fileName);
      } catch (error) {
        console.warn(`Failed to delete file ${fileName}:`, error);
        return null; // Continue without failing
      }
    }
  }

  /**
   * Delete a single file
   */
  async deleteFile(fileName, deleteFromCloudflare = true) {
    console.log(`🗑️ FirebaseService.deleteFile called for: ${fileName} (deleteFromCloudflare: ${deleteFromCloudflare})`);
    
    try {
      // Delete from Firebase Storage
      console.log(`🔥 Deleting from Firebase Storage: ${fileName}`);
      const filePath = await this.getFilePath(fileName);
      const fileRef = ref(this.storage, filePath);
      await deleteObject(fileRef);
      console.log(`✅ Firebase Storage deletion successful: ${fileName}`);
      
      // Also delete from Cloudflare if requested and if it's a video or image file
      if (deleteFromCloudflare && (fileName.includes('_video') || fileName.includes('_image') || fileName.includes('_thumb'))) {
        console.log(`🔍 Deleting from Cloudflare as requested: ${fileName}`);
        try {
          // Extract just the filename from the full path for Cloudflare
          const filenameOnly = fileName.split('/').pop();
          console.log(`☁️ Deleting from Cloudflare: ${filenameOnly} (extracted from ${fileName})`);
          const { default: cloudflareService } = await import('./CloudflareService.js');
          await cloudflareService.deleteFile(filenameOnly);
          console.log(`✅ Cloudflare deletion successful: ${filenameOnly}`);
        } catch (cloudflareError) {
          console.warn(`⚠️ Cloudflare deletion failed for ${fileName}:`, cloudflareError);
          // Don't fail the entire operation if Cloudflare deletion fails
        }
      } else {
        console.log(`⚠️ Skipping Cloudflare deletion for ${fileName} (deleteFromCloudflare: ${deleteFromCloudflare})`);
      }
      
      return true;
    } catch (error) {
      // Don't throw fatal errors for delete failures
      if (error.code === 'storage/object-not-found') {
        console.log(`File ${fileName} not found for deletion (already deleted or doesn't exist)`);
      } else {
        console.warn(`Error deleting file ${fileName}:`, error);
      }
      // Return resolved promise to continue without failing
      return Promise.resolve();
    }
  }

  /**
   * Get files with filter
   */
  async getFiles(fileNameFilter, folderName) {
    console.log('🔍 FirebaseService.getFiles called:', { fileNameFilter, folderName });
    
    try {
      const currentUser = await userService.getCurrentUser();
      if (!currentUser) {
        throw new Error('No authenticated user found');
      }

      const folderPath = folderName ? folderName : currentUser.uid;
      console.log(`📁 Searching in folder: ${folderPath}`);
      
      const listRef = ref(this.storage, folderPath);
      
      const { listAll } = await import('firebase/storage');
      const res = await listAll(listRef);
      
      console.log(`📁 Found ${res.items.length} total files in folder ${folderPath}`);
      
      const files = [];
      res.items.forEach((itemRef) => {
        console.log(`📄 Checking file: ${itemRef.name} (matches "${fileNameFilter}": ${itemRef.name.indexOf(fileNameFilter) !== -1})`);
        if (itemRef.name.indexOf(fileNameFilter) !== -1) {
          files.push(itemRef.fullPath);
          console.log(`✅ Added to deletion list: ${itemRef.fullPath}`);
        }
      });
      
      console.log(`🔍 getFiles returning ${files.length} files matching filter "${fileNameFilter}"`);
      return files;
    } catch (error) {
      console.error(`❌ Error getting files with filter ${fileNameFilter}:`, error);
      return [];
    }
  }

  /**
   * Delete multiple files with filter
   */
  async deleteFiles(fileNameFilter, folderName) {
    console.log('🗂️ FirebaseService.deleteFiles called:', { fileNameFilter, folderName });
    
    // for delete all by mistake protection
    if (fileNameFilter) {
      try {
        console.log('🔍 Searching for files to delete...');
        const files = await this.getFiles(fileNameFilter, folderName);
        console.log(`🔍 Found ${files.length} files matching filter "${fileNameFilter}":`, files);
        
        if (files.length === 0) {
          console.log('⚠️ No files found to delete');
          return [];
        }
        
        const promises = [];
        files.forEach(file => {
          console.log(`🗑️ Adding file to deletion queue: ${file}`);
          promises.push(this.deleteFile(file));
        });
        
        console.log(`⚡ Starting deletion of ${files.length} files...`);
        const results = await Promise.allSettled(promises);
        
        const successfulDeletes = results.filter(result => result.status === 'fulfilled').length;
        const failedDeletes = results.filter(result => result.status === 'rejected').length;
        
        console.log(`✅ File deletion completed: ${successfulDeletes} successful, ${failedDeletes} failed for filter: ${fileNameFilter}`);
        
        // Log details of failed deletions
        if (failedDeletes > 0) {
          results.forEach((result, index) => {
            if (result.status === 'rejected') {
              console.error(`❌ Failed to delete file ${files[index]}:`, result.reason);
            }
          });
        }
        
        return results;
      } catch (error) {
        console.error(`❌ Error deleting files with filter ${fileNameFilter}:`, error);
        return [];
      }
    } else {
      console.warn('⚠️ deleteFiles called without fileNameFilter - operation blocked for safety');
      return [];
    }
  }

  /**
   * Wait for image files to be created with retry logic
   */
  async waitForImageFilesCreated(fileName, attempt = 0) {
    const jpgFileName = fileName.replace(".jpeg", ".jpeg").replace(".jpg", ".jpeg").replace(".png", ".jpeg");
    
    if (jpgFileName.indexOf(".jpeg") !== -1) {
      const fileNames = this.supportedImageResolutions.map(res => 
        jpgFileName.replace(".jpeg", `_${res}.jpeg`)
      );
      
      const promises = fileNames.map(async (fileName) => {
        try {
          const filePath = await this.getFilePath(fileName);
          const fileRef = ref(this.storage, filePath);
          return getDownloadURL(fileRef);
        } catch (error) {
          // If file doesn't exist yet, throw error to trigger retry
          if (error.code === 'storage/object-not-found') {
            throw error;
          }
          // For other errors, log but don't retry
          console.warn(`Error getting download URL for ${fileName}:`, error);
          return null;
        }
      });

      try {
        const results = await Promise.all(promises);
        // Check if all files are available
        const allFilesExist = results.every(url => url !== null);
        
        if (!allFilesExist && attempt < 10) {
          console.log(`Waiting for image files to be created, attempt ${attempt + 1}/10`);
          await this.wait(3000);
          return this.waitForImageFilesCreated(fileName, attempt + 1);
        } else if (!allFilesExist) {
          console.warn(`Some image files were not created after 10 attempts: ${fileNames.join(', ')}`);
          return results.filter(url => url !== null);
        }
        
        return results;
      } catch (error) {
        if (attempt < 10) {
          console.log(`Retrying image file creation, attempt ${attempt + 1}/10`);
          await this.wait(3000);
          return this.waitForImageFilesCreated(fileName, attempt + 1);
        } else {
          console.error(`Failed to create image files after 10 attempts: ${fileNames.join(', ')}`);
          return [];
        }
      }
    } else {
      return [];
    }
  }

  /**
   * Wait for video image files to be created with retry logic
   */
  async waitForVideoImageFilesCreated(fileName, attempt = 0) {
    if (fileName.indexOf(".mp4") !== -1) {
      const jpgFileName = fileName
        .replace("_video", "_video_thumb")
        .replace(".mp4", ".jpeg")

      
      if (jpgFileName.indexOf(".jpeg") !== -1) {
        const fileNames = this.supportedImageResolutions.map(res => 
          jpgFileName.replace(".jpeg", `_${res}.jpeg`)
        );
        
        const promises = fileNames.map(async (fileName) => {
          try {
            const filePath = await this.getFilePath(fileName);
            const fileRef = ref(this.storage, filePath);
            return getDownloadURL(fileRef);
          } catch (error) {
            // If file doesn't exist yet, throw error to trigger retry
            if (error.code === 'storage/object-not-found') {
              throw error;
            }
            // For other errors, log but don't retry
            console.warn(`Error getting download URL for video thumbnail ${fileName}:`, error);
            return null;
          }
        });

        try {
          const results = await Promise.all(promises);
          // Check if all files are available
          const allFilesExist = results.every(url => url !== null);
          
          if (!allFilesExist && attempt < 30) {
            console.log(`Waiting for video thumbnail files to be created, attempt ${attempt + 1}/30`);
            await this.wait(3000);
            return this.waitForVideoImageFilesCreated(fileName, attempt + 1);
          } else if (!allFilesExist) {
            console.warn(`Some video thumbnail files were not created after 30 attempts: ${fileNames.join(', ')}`);
            return results.filter(url => url !== null);
          }
          
          return results;
        } catch (error) {
          if (attempt < 30) {
            console.log(`Retrying video thumbnail file creation, attempt ${attempt + 1}/30`);
            await this.wait(3000);
            return this.waitForVideoImageFilesCreated(fileName, attempt + 1);
          } else {
            console.error(`Failed to create video thumbnail files after 30 attempts: ${fileNames.join(', ')}`);
            return [];
          }
        }
      } else {
        return [];
      }
    } else {
      return [];
    }
  }

  /**
   * Get file URL
   */
  async getFileUrl(fileName) {
    try {
      const filePath = await this.getFilePath(fileName);
      const fileRef = ref(this.storage, filePath);
      return getDownloadURL(fileRef);
    } catch (error) {
      console.error('Error getting file URL:', error);
      throw error;
    }
  }

  /**
   * Upload file with progress callback
   */
  async uploadFile(fileName, blob) {
    try {
      // If blob is already a URL, return it with size conversion
      if (blob.indexOf("http") === 0) {
        return blob.replace("_640x360.", "_1920x1080.");
      }

      // Get file path for current user
      const filePath = await this.getFilePath(fileName);
      const fileRef = ref(this.storage, filePath);

      console.log(`Uploading file: ${fileName} to path: ${filePath}`);

      // Delete existing image files first (don't fail if deletion fails)
      try {
        await this.deleteImageFiles(fileName);
      } catch (error) {
        console.warn(`Failed to delete existing image files for ${fileName}:`, error);
        // Continue with upload even if deletion fails
      }

      // Upload the new file
      await uploadString(fileRef, blob, 'data_url');
      console.log(`File uploaded successfully: ${fileName}`);

      // Wait for image files to be created (but don't fail if they don't exist)
      try {
        await this.waitForImageFilesCreated(fileName);
      } catch (error) {
        console.warn(`Image files not created for ${fileName}:`, error);
      }

      try {
        await this.waitForVideoImageFilesCreated(fileName);
      } catch (error) {
        console.warn(`Video thumbnail files not created for ${fileName}:`, error);
      }

      // Try to get the URL of the high-resolution version
      const highResFileName = fileName
        .replace(".jpeg", "_1920x1920.jpeg")
        .replace(".jpg", "_1920x1920.jpeg")
        .replace(".png", "_1920x1920.jpeg");

      try {
        return await this.getFileUrl(highResFileName);
      } catch (error) {
        console.warn(`High-res file not found, trying original file: ${fileName}`);
        // If high-res version doesn't exist, try the original file
        return await this.getFileUrl(fileName);
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      throw error;
    }
  }

  /**
   * Utility function to wait for specified milliseconds
   */
  wait(ms) {
    return new Promise((resolve) => {
      setTimeout(resolve, ms);
    });
  }

  /**
   * Get Firebase user folder URL
   */
  async getFirebaseUserFolderUrl() {
    try {
      const currentUser = await userService.getCurrentUser();
      if (!currentUser) {
        throw new Error('No authenticated user found');
      }

      return `https://${this.storage.host}/v0/b/${this.storage._bucket.bucket}/o/${currentUser.uid}%2F`;
    } catch (error) {
      console.error('Error getting Firebase user folder URL:', error);
      throw error;
    }
  }

  /**
   * Get thumbnail URL for video
   */
  async getThumbUrl(videoUrl, isUniversal) {
    try {
      // Import CloudflareService dynamically to avoid circular dependencies
      const { default: cloudflareService } = await import('./CloudflareService.js');
      
      if (videoUrl.indexOf(cloudflareService.getPublicFileUrl("")) !== -1) {
        // replace the cloudflare part of the url by the firebase bucket url
        const firebaseUserFolderUrl = await this.getFirebaseUserFolderUrl();
        videoUrl = videoUrl.replace(cloudflareService.getPublicFileUrl(""), firebaseUserFolderUrl);
        if (videoUrl.indexOf("?alt=media") === -1) {
          videoUrl += "?alt=media";
        }
      }
      
      return videoUrl
        .replace('_video', '_video_thumb')
        .replace('.mp4', isUniversal ? '_640x640.jpeg' : '_640x360.jpeg')

    } catch (error) {
      console.error('Error getting thumbnail URL:', error);
      throw error;
    }
  }

  /**
   * Send admin email using Firebase Functions
   * @param {string} subject - Email subject
   * @param {string} text - Plain text content
   * @param {string} html - HTML content
   * @param {Array} attachments - Email attachments
   * @returns {Promise} Firebase function call result
   */
  sendAdminEmail(subject, text, html, attachments) {
    try {
      return httpsCallable(this.functions, 'sendAdminEmail')({
        subject: subject, 
        text: text, 
        html: html, 
        attachments: attachments
      });
    } catch (error) {
      console.error('Error calling sendAdminEmail function:', error);
      throw error;
    }
  }

  /**
   * Send email to specific user using Firebase Functions
   * @param {string} uid - User ID
   * @param {string} subject - Email subject
   * @param {string} text - Plain text content
   * @param {string} html - HTML content
   * @param {Array} attachments - Email attachments
   * @returns {Promise} Firebase function call result
   */
  sendUserEmail(uid, subject, text, html, attachments) {
    try {
      return httpsCallable(this.functions, 'sendUserEmail')({
        uid: uid, 
        subject: subject, 
        text: text, 
        html: html, 
        attachments: attachments
      });
    } catch (error) {
      console.error('Error calling sendUserEmail function:', error);
      throw error;
    }
  }

  /**
   * Send email to multiple users by admin using Firebase Functions
   * @param {Array} uids - Array of user IDs
   * @param {string} subject - Email subject
   * @param {string} text - Plain text content
   * @param {string} html - HTML content
   * @param {Array} attachments - Email attachments
   * @returns {Promise} Firebase function call result
   */
  sendEmailByAdmin(uids, subject, text, html, attachments) {
    try {
      return httpsCallable(this.functions, 'sendEmailByAdmin')({
        uids: uids, 
        subject: subject, 
        text: text, 
        html: html, 
        attachments: attachments
      });
    } catch (error) {
      console.error('Error calling sendEmailByAdmin function:', error);
      throw error;
    }
  }

  /**
   * Send password reset email
   * @param {string} email - User's email address
   * @returns {Promise} Firebase auth result
   */
  sendPasswordResetEmail(email) {
    try {
      return sendPasswordResetEmail(this.auth, email);
    } catch (error) {
      console.error('Error sending password reset email:', error);
      throw error;
    }
  }

  /**
   * Send email verification to current user
   * @param {string} url - URL to redirect after verification (optional)
   * @returns {Promise} Firebase auth result
   */
  sendEmailVerification(url = null) {
    try {
      const currentUser = this.auth.currentUser;
      if (!currentUser) {
        throw new Error('No authenticated user found');
      }

      const actionCodeSettings = url ? { url } : { url: window.location.href };
      return sendEmailVerification(currentUser, actionCodeSettings);
    } catch (error) {
      console.error('Error sending email verification:', error);
      throw error;
    }
  }
}

// Create singleton instance
const firebaseService = new FirebaseService();

export default firebaseService; 